import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";

export function PaymentSuccess() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
      } catch (e) {
        console.debug("Payment status poll retry after transient error:", e);
      }
      if (tries < 15) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-10 text-center">
        <Link to="/" className="inline-block mb-8"><Logo size="md" /></Link>
        {status === "checking" && (
          <div data-testid="pay-checking">
            <Loader2 className="h-12 w-12 mx-auto text-blue-600 animate-spin" />
            <p className="mt-5 text-slate-300">{t("pay_processing")}</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="pay-success">
            <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-500" />
            <h1 className="mt-5 font-heading text-2xl font-bold text-white">{t("pay_success_title")}</h1>
            <p className="mt-2 text-slate-300">{t("pay_success_body")}</p>
            <Button className="mt-7 w-full h-11" data-testid="pay-go-dashboard" onClick={() => navigate("/analysis/new")}>{t("pay_go")}</Button>
          </div>
        )}
        {(status === "timeout" || status === "error") && (
          <div data-testid="pay-timeout">
            <Loader2 className="h-12 w-12 mx-auto text-slate-300" />
            <p className="mt-5 text-slate-300">{t("pay_processing")}</p>
            <Button variant="outline" className="mt-6 w-full" onClick={() => navigate("/dashboard")}>{t("pay_go")}</Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-10 text-center" data-testid="pay-cancel">
        <Link to="/" className="inline-block mb-8"><Logo size="md" /></Link>
        <XCircle className="h-14 w-14 mx-auto text-slate-500" />
        <h1 className="mt-5 font-heading text-2xl font-bold text-white">{t("pay_cancel_title")}</h1>
        <p className="mt-2 text-slate-300">{t("pay_cancel_body")}</p>
        <Button className="mt-7 w-full h-11" onClick={() => navigate("/pricing")}>{t("pay_pricing")}</Button>
      </div>
    </div>
  );
}
